import React from "react";
import { motion } from "framer-motion";

// Premium "fade up" easing — feels close to Apple/Linear style
const EASE = [0.22, 1, 0.36, 1];

const fadeUp = (y = 48, duration = 0.9, delay = 0) => ({
  hidden: { opacity: 0, y, scale: 0.96 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration, delay, ease: EASE },
  },
});

/**
 * Single fade-up reveal. Triggers when 15% of the element scrolls into view.
 * Runs once by default so already-viewed sections don't re-animate on scroll back.
 */
export function Reveal({
  children,
  className,
  delay = 0,
  y = 48,
  duration = 0.9,
  amount = 0.2,
  once = true,
}) {
  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="show"
      viewport={{ once, amount }}
      variants={fadeUp(y, duration, delay)}
    >
      {children}
    </motion.div>
  );
}

/**
 * Parent container that staggers reveals on its <RevealItem> children.
 */
export function RevealStagger({
  children,
  className,
  stagger = 0.14,
  delayChildren = 0.1,
  amount = 0.2,
  once = true,
}) {
  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="show"
      viewport={{ once, amount }}
      variants={{
        hidden: {},
        show: {
          transition: { staggerChildren: stagger, delayChildren },
        },
      }}
    >
      {children}
    </motion.div>
  );
}

/**
 * Child of <RevealStagger>. Inherits the parent's animation states.
 */
export function RevealItem({ children, className, y = 48 }) {
  return (
    <motion.div
      className={className}
      variants={{
        hidden: { opacity: 0, y, scale: 0.94 },
        show: {
          opacity: 1,
          y: 0,
          scale: 1,
          transition: { duration: 0.8, ease: EASE },
        },
      }}
    >
      {children}
    </motion.div>
  );
}

export { EASE };
